'use client';

import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useRemoveRoleFromUserMutation } from '../services/users.mutations';
import type { User } from '../types';

interface RemoveRoleFromUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  roleName: string | null;
}

export function RemoveRoleFromUserDialog({
  open,
  onOpenChange,
  user,
  roleName
}: RemoveRoleFromUserDialogProps) {
  const t = useTranslations('users');
  const removeMutation = useRemoveRoleFromUserMutation();

  async function handleConfirm() {
    if (!user || !roleName) return;
    try {
      await removeMutation.mutateAsync({
        userId: user.id,
        data: { role: roleName }
      });
      onOpenChange(false);
    } catch {
      // handled by global MutationCache
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('removeRole')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('removeRoleDescription', {
              roleName: roleName ?? '',
              userName: user?.name ?? ''
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={cn(
              'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            )}
          >
            {removeMutation.isPending && (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            )}
            {t('remove')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
